import { type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConfiguration } from '@/contexts/ConfigurationContext';

interface PreviewNavigationBlockerProps {
  children: ReactNode;
}

/**
 * Blocks navigation in preview mode - users can only view the current page
 */
export function PreviewNavigationBlocker({ children }: PreviewNavigationBlockerProps) {
  const { isPreviewMode, isDemoMode } = useConfiguration();
  const location = useLocation();

  useEffect(() => {
    // Only block navigation in preview mode, not demo mode
    // Demo mode should allow navigation so users can explore the journey
    if (!isPreviewMode || isDemoMode) return;

    // Store the initial pathname when entering preview mode
    const initialPath = location.pathname;

    // Intercept link clicks
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]');
      
      if (link) {
        const href = link.getAttribute('href');
        if (href && href.startsWith('/') && href !== initialPath) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          console.log('[PreviewNavigationBlocker] Blocked navigation to:', href);
          return false;
        }
      }
    };

    // Override window.history methods to prevent navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      const [, , url] = args;
      if (url) {
        const urlPath = new URL(url, window.location.origin).pathname;
        if (urlPath !== initialPath) {
          console.log('[PreviewNavigationBlocker] Blocked pushState to:', urlPath);
          return;
        }
      }
      return originalPushState.apply(window.history, args);
    };

    window.history.replaceState = function(...args) {
      const [, , url] = args;
      if (url) {
        const urlPath = new URL(url, window.location.origin).pathname;
        if (urlPath !== initialPath) {
          console.log('[PreviewNavigationBlocker] Blocked replaceState to:', urlPath);
          return;
        }
      }
      return originalReplaceState.apply(window.history, args);
    };

    // Listen for popstate (back/forward buttons)
    const handlePopState = (e: PopStateEvent) => {
      if (window.location.pathname !== initialPath) {
        e.preventDefault();
        window.history.pushState(null, '', `${initialPath}${window.location.search}`);
        console.log('[PreviewNavigationBlocker] Blocked popstate navigation');
      }
    };

    // Add event listeners
    document.addEventListener('click', handleClick, true);
    window.addEventListener('popstate', handlePopState);

    // Monitor for pathname changes and reset if needed
    const checkPath = () => {
      if (window.location.pathname !== initialPath) {
        window.history.replaceState(null, '', `${initialPath}${window.location.search}`);
      }
    };

    const interval = setInterval(checkPath, 100);

    return () => {
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('popstate', handlePopState);
      clearInterval(interval);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [isPreviewMode, isDemoMode, location.pathname]);

  return <>{children}</>;
}

